import { useEffect, useState } from 'react';
import { fetchSavedPlacesPage, type SavedPlaceItem } from '../mapSavedPlacesApi';

type Props = {
  compact?: boolean;
  limit?: number;
  onOpenVenue: (venueId: string) => void;
  onShowAll?: () => void;
};

export function SavedPlacesSection({
  compact = false,
  limit = 50,
  onOpenVenue,
  onShowAll,
}: Props) {
  const [items, setItems] = useState<SavedPlaceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void fetchSavedPlacesPage(null, limit)
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setCursor(page.pagination.next_cursor);
        setHasMore(page.pagination.has_more);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить сохранённые места');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [limit]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;

    setLoadingMore(true);
    try {
      const page = await fetchSavedPlacesPage(cursor, 50);
      setItems((current) => [...current, ...page.items]);
      setCursor(page.pagination.next_cursor);
      setHasMore(page.pagination.has_more);
    } catch {
      setError('Не удалось загрузить продолжение списка');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className={`profile-reference-card profile-saved-workspace${compact ? ' is-compact' : ''}`}>
      <div className="profile-reference-card__heading">
        <h2>Хочу посетить</h2>
        {onShowAll && <button type="button" onClick={onShowAll}>Смотреть все</button>}
      </div>

      {loading ? (
        <p className="profile-inline-state">Загружаем сохранённые места…</p>
      ) : error && items.length === 0 ? (
        <p className="profile-inline-state">{error}</p>
      ) : items.length === 0 ? (
        <p className="profile-inline-state">Сохраните место на карте — оно появится здесь.</p>
      ) : (
        <div className="profile-compact-list">
          {items.map((item) => (
            <button type="button" key={item.venue.id} onClick={() => onOpenVenue(item.venue.id)}>
              {item.venue.cover_url ? (
                <img src={item.venue.cover_url} alt="" />
              ) : (
                <span className="profile-place-placeholder" />
              )}
              <span>
                <strong>{item.venue.name ?? 'Место Хоппера'}</strong>
                <small>{[item.venue.category, item.venue.city.name].filter(Boolean).join(' · ') || 'Сохранённое место'}</small>
              </span>
              {item.venue.hopper_score !== null && <b>{item.venue.hopper_score}</b>}
            </button>
          ))}
        </div>
      )}

      {!compact && error && <p className="profile-inline-state">{error}</p>}
      {!compact && hasMore && (
        <button
          className="profile-secondary-button profile-load-more"
          type="button"
          disabled={loadingMore}
          onClick={() => void loadMore()}
        >
          {loadingMore ? 'Загружаем…' : 'Показать ещё'}
        </button>
      )}
    </section>
  );
}
